import { useState, useEffect } from 'react'
import { useMCPStore, MCPServer, MCPTool } from '../stores/mcpStore'
import { 
  Plus, 
  Trash2, 
  Play, 
  Square, 
  RefreshCw,
  Terminal,
  ChevronRight,
  ChevronDown,
  Search,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react'

export default function MCPServersPage() {
  const {
    servers,
    isLoading,
    error,
    fetchServers,
    createServer,
    deleteServer,
    startServer,
    stopServer,
    fetchAllTools,
    allTools,
    clearError
  } = useMCPStore()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set())
  const [serverTools, setServerTools] = useState<Record<string, MCPTool[]>>({})
  const [selectedTool, setSelectedTool] = useState<{ serverId: string, tool: MCPTool } | null>(null)
  const [toolArgs, setToolArgs] = useState<string>('{}')
  const [toolResult, setToolResult] = useState<string>('')
  const [toolLoading, setToolLoading] = useState(false)

  // New server form state
  const [newServer, setNewServer] = useState({
    name: '',
    command: '',
    args: '',
    env: '',
    description: ''
  })

  useEffect(() => {
    fetchServers()
    fetchAllTools()
  }, [fetchServers, fetchAllTools])

  const toggleExpanded = async (serverId: string) => {
    const newExpanded = new Set(expandedServers)
    if (newExpanded.has(serverId)) {
      newExpanded.delete(serverId)
    } else {
      newExpanded.add(serverId)
      // Fetch tools for this server
      if (!serverTools[serverId]) {
        const tools = await useMCPStore.getState().fetchTools(serverId)
        setServerTools(prev => ({ ...prev, [serverId]: tools }))
      }
    }
    setExpandedServers(newExpanded)
  }

  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Handle args - support both comma-separated and JSON array format
      let args: string[] = []
      if (newServer.args) {
        const trimmed = newServer.args.trim()
        if (trimmed.startsWith('[')) {
          // JSON array format: ["/path1", "/path2"]
          try {
            args = JSON.parse(trimmed)
          } catch {
            // Invalid JSON, fallback to comma-separated
            args = trimmed.split(',').map(a => a.trim()).filter(Boolean)
          }
        } else {
          // Comma-separated format: /path1, /path2
          args = trimmed.split(',').map(a => a.trim()).filter(Boolean)
        }
      }
      
      const env = newServer.env ? Object.fromEntries(
        newServer.env.split(',').map(pair => {
          const [key, value] = pair.split('=')
          return [key.trim(), value.trim()]
        })
      ) : {}

      await createServer({
        name: newServer.name,
        command: newServer.command,
        args,
        env,
        description: newServer.description
      })
      setShowCreateModal(false)
      setNewServer({ name: '', command: '', args: '', env: '', description: '' })
    } catch (err) {
      console.error('Failed to create server:', err)
    }
  }

  const handleCallTool = async () => {
    if (!selectedTool) return
    setToolLoading(true)
    setToolResult('')
    try {
      const args = JSON.parse(toolArgs)
      const result = await useMCPStore.getState().callTool(selectedTool.serverId, selectedTool.tool.name, args)
      setToolResult(JSON.stringify(result, null, 2))
    } catch (err) {
      setToolResult(`Error: ${err}`)
    } finally {
      setToolLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />
      case 'connecting':
        return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />
      default:
        return <XCircle className="w-4 h-4 text-dark-400" />
    }
  }

  return (
    <div className="h-full overflow-auto bg-dark-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">MCP Servers</h1>
          <p className="text-dark-400 mt-1">Manage Model Context Protocol servers and tools</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Server
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={clearError} className="text-dark-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && servers.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      )}

      {/* Server List */}
      {!isLoading && servers.length === 0 && (
        <div className="text-center py-12">
          <Terminal className="w-12 h-12 text-dark-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No MCP Servers</h3>
          <p className="text-dark-400 mb-4">Get started by adding your first MCP server</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Add Your First Server
          </button>
        </div>
      )}

      {/* Servers */}
      <div className="space-y-3">
        {servers.map(server => (
          <div
            key={server.id}
            className="bg-dark-800 rounded-lg border border-dark-700 overflow-hidden"
          >
            {/* Server Header */}
            <div className="flex items-center gap-4 p-4">
              <button
                onClick={() => toggleExpanded(server.id)}
                className="text-dark-400 hover:text-white transition-colors"
              >
                {expandedServers.has(server.id) ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-white truncate">{server.name}</h3>
                  {getStatusIcon(server.status)}
                </div>
                <p className="text-sm text-dark-400 truncate font-mono">{server.command}</p>
                {server.lastError && (
                  <p className="text-sm text-red-400 mt-1">{server.lastError}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {server.status === 'connected' ? (
                  <button
                    onClick={() => stopServer(server.id)}
                    className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
                    title="Stop Server"
                  >
                    <Square className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => startServer(server.id)}
                    className="p-2 text-dark-400 hover:text-green-400 hover:bg-dark-700 rounded-lg transition-colors"
                    title="Start Server"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => fetchServers()}
                  className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this server?')) {
                      deleteServer(server.id)
                    }
                  }}
                  className="p-2 text-dark-400 hover:text-red-400 hover:bg-dark-700 rounded-lg transition-colors"
                  title="Delete Server"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedServers.has(server.id) && (
              <div className="border-t border-dark-700 p-4">
                {serverTools[server.id] ? (
                  <div>
                    <h4 className="text-sm font-medium text-dark-300 mb-3">Available Tools</h4>
                    {serverTools[server.id].length === 0 ? (
                      <p className="text-dark-400 text-sm">No tools available</p>
                    ) : (
                      <div className="grid gap-2">
                        {serverTools[server.id].map(tool => (
                          <button
                            key={tool.name}
                            onClick={() => setSelectedTool({ serverId: server.id, tool })}
                            className="text-left p-3 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-primary-400 text-sm">{tool.name}</span>
                            </div>
                            <p className="text-dark-400 text-sm mt-1">{tool.description}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-dark-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading tools...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* All Tools Section */}
      {allTools.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-bold text-white mb-4">All Available Tools</h2>
          <div className="grid gap-4">
            {allTools.map(({ serverId, serverName, tools }) => (
              <div key={serverId} className="bg-dark-800 rounded-lg border border-dark-700 p-4">
                <h3 className="font-medium text-white mb-2">{serverName}</h3>
                <div className="grid gap-2">
                  {tools.map(tool => (
                    <div key={tool.name} className="p-3 bg-dark-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-primary-400 text-sm">{tool.name}</span>
                      </div>
                      <p className="text-dark-400 text-sm mt-1">{tool.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg border border-dark-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-bold text-white">Add MCP Server</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-dark-400 hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateServer} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Name</label>
                <input
                  type="text"
                  value={newServer.name}
                  onChange={e => setNewServer({ ...newServer, name: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="My MCP Server"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Command</label>
                <input
                  type="text"
                  value={newServer.command}
                  onChange={e => setNewServer({ ...newServer, command: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500 font-mono"
                  placeholder="npx @modelcontextprotocol/server-filesystem"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Arguments (paths, space or comma-separated)
                </label>
                <input
                  type="text"
                  value={newServer.args}
                  onChange={e => setNewServer({ ...newServer, args: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500 font-mono"
                  placeholder="/opt/apps/halcode-webapp/mcp-files/admin-id"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Environment Variables (key=value, comma-separated)
                </label>
                <input
                  type="text"
                  value={newServer.env}
                  onChange={e => setNewServer({ ...newServer, env: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500 font-mono"
                  placeholder="API_KEY=abc123,DEBUG=true"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Description</label>
                <textarea
                  value={newServer.description}
                  onChange={e => setNewServer({ ...newServer, description: e.target.value })}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
                  placeholder="What does this server do?"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  Create Server
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tool Call Modal */}
      {selectedTool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg border border-dark-700 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <div>
                <h2 className="text-lg font-bold text-white">{selectedTool.tool.name}</h2>
                <p className="text-dark-400 text-sm">{selectedTool.tool.description}</p>
              </div>
              <button
                onClick={() => setSelectedTool(null)}
                className="text-dark-400 hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">
                  Arguments (JSON)
                </label>
                <textarea
                  value={toolArgs}
                  onChange={e => setToolArgs(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-primary-500"
                  placeholder='{"param": "value"}'
                  rows={6}
                />
              </div>

              <button
                onClick={handleCallTool}
                disabled={toolLoading}
                className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {toolLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Calling Tool...
                  </>
                ) : (
                  <>
                    <Terminal className="w-4 h-4" />
                    Call Tool
                  </>
                )}
              </button>

              {toolResult && (
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-1">Result</label>
                  <pre className="w-full px-3 py-2 bg-dark-900 border border-dark-700 rounded-lg text-dark-200 font-mono text-sm overflow-auto max-h-64">
                    {toolResult}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
